import { MEDIA_LIMITS } from '../media/mediaLimits';

/**
 * Ses kaydının süre ve ücret kuralı (3.1.7.3 · 9.1 · D-541).
 *
 * Burası saf: kayıt cihazını, dosyayı ve sunucuyu tanımaz. Sebebi, bu iki
 * kuralın kararın kendisi olması — native bir modül gelmeden de sınanabilmeli
 * ve tek bir yerde durmalı.
 */

/**
 * SERBEST gönderimin süre tavanı — **60 saniyeden 60 dakikaya çıktı**
 * (9.1.6 · D-559).
 *
 * Eski 60 saniyenin gerekçesi şuydu: ücret dakika başına ilan edildiği için
 * sınır 60 saniye olunca profildeki sayı tek bir gönderimin tavanı oluyordu.
 * Koruma bir **duvardı**.
 *
 * D-559 o korumayı duvardan **anlık tutar göstergesine** devretti: kendi
 * parasını harcayan kişiye ne harcadığını göstermek yeterli, çünkü ödeyen
 * kayıt düğmesini basılı tutan kişinin ta kendisi. Taksimetre olmadan bu sayı
 * yükseltilseydi koruma yerine hiçbir şey konmamış olurdu.
 *
 * Sayı burada yazılı değil: medya tavanından türüyor. Üçüncü bir sabit,
 * gönderilebilen süre ile ücretlenen sürenin ayrışmasına izin verirdi.
 */
export const VOICE_NOTE_MAX_SECONDS = MEDIA_LIMITS.voice.maxDurationSeconds;

/**
 * Ücret saniye hesabıyla işler, tam dakikaya yuvarlanmaz.
 *
 * Aramalar zaten saniyeyle faturalanıyor (`billed_seconds`); beş saniyelik bir
 * kayda tam dakika almak sessiz bir zam olurdu. Yuvarlama YUKARI değil, oransal.
 *
 * Sıfır ve negatif süre sıfır ücret döndürür: kaydedilmemiş bir şey için para
 * istenmez. Süre sınırı aşarsa sınırda kesilir — kayıt zaten orada durdurulur,
 * ama ücret hesabı buna ikinci kez bakar, çünkü ücreti doğru tutmak kayıt
 * cihazının doğru davranmasına bırakılamaz.
 */
export function voiceNoteCost(seconds: number, pricePerMinute: number): number {
  if (!Number.isFinite(seconds) || seconds <= 0) return 0;
  if (!Number.isFinite(pricePerMinute) || pricePerMinute <= 0) return 0;
  const billed = Math.min(seconds, VOICE_NOTE_MAX_SECONDS);
  return (billed / 60) * pricePerMinute;
}

/** Kayıt sınıra ulaştı mı — kayıt cihazı kendini bununla durdurur. */
export function voiceNoteLimitReached(seconds: number): boolean {
  return Number.isFinite(seconds) && seconds >= VOICE_NOTE_MAX_SECONDS;
}

/** Sınıra kalan süre; geri sayım göstermek için. Sınırı aşarsa sıfır. */
export function voiceNoteRemainingSeconds(seconds: number): number {
  if (!Number.isFinite(seconds) || seconds <= 0) return VOICE_NOTE_MAX_SECONDS;
  return Math.max(0, VOICE_NOTE_MAX_SECONDS - seconds);
}

/**
 * Taksimetre (9.1.6 · D-559).
 *
 * Serbest gönderimde kimse tavan onaylamıyor ve ödeyen, kayıt düğmesini
 * basılı tutan kişi. Bu yüzden korumanın tamamı **görünürlük**: kayıt sürerken
 * o ana kadarki tutar ekranda duruyor ve gönderimde fiyat damgası son rakamı
 * onaylatıyor.
 *
 * Tutar `voiceNoteCost` ile aynı formülden geliyor — ayrı bir hesap, ekranda
 * gösterilen ile kesilen arasında fark doğururdu ki 3.10.5'in tam olarak
 * önlemeye çalıştığı şey budur.
 */
export type VoiceMeterReading = {
  seconds: number;
  amount: number;
  remainingSeconds: number;
  limitReached: boolean;
};

export function voiceMeterReading(seconds: number, pricePerMinute: number): VoiceMeterReading {
  const gecerli = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  return {
    seconds: gecerli,
    amount: voiceNoteCost(gecerli, pricePerMinute),
    remainingSeconds: voiceNoteRemainingSeconds(gecerli),
    limitReached: voiceNoteLimitReached(gecerli),
  };
}

/**
 * Sipariş teslimindeki okuma (9.1.11).
 *
 * Serbest gönderimden iki farkı var ve ikisi de paradan geliyor. Tavan 60
 * dakika değil, **siparişin kendi süresi**: ısmarlayan neye razı olduysa
 * sunucu da ücreti orada kesiyor, ekranın daha uzun kaydetmeye izin vermesi
 * satıcıya boşa kayıt yaptırırdı. Ve **artan bir tutar yok**: para sipariş
 * anında emanete alındı, kayıt sürerken büyüyen bir rakam göstermek yalan
 * olurdu. Gösterilecek doğru sayı, tavana kalan süredir.
 */
export function voiceOrderMeterReading(
  seconds: number,
  orderSeconds: number
): { seconds: number; remainingSeconds: number; limitReached: boolean } {
  const gecerli = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const tavan = Number.isFinite(orderSeconds) && orderSeconds > 0
    ? Math.min(orderSeconds, VOICE_NOTE_MAX_SECONDS) : 0;
  return {
    seconds: gecerli,
    remainingSeconds: Math.max(0, tavan - gecerli),
    limitReached: tavan > 0 && gecerli >= tavan,
  };
}
